import { useEffect, useMemo, useState } from 'react'
import supabase from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const inputClass = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100'
const blankArticle = () => ({ id: null, service_id: '', asset_model_id: '', article_type: 'howto', title: '', content: '', document_url: '', source_url: '', version: '', tags: '' })

export default function KnowledgeManager() {
  const [rows, setRows] = useState([])
  const [brands, setBrands] = useState([])
  const [models, setModels] = useState([])
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [type, setType] = useState('all')
  const [editorOpen, setEditorOpen] = useState(false)
  const [form, setForm] = useState(blankArticle())
  const [saving, setSaving] = useState(false)
  const [catalogOpen, setCatalogOpen] = useState(false)
  const [brandName, setBrandName] = useState('')
  const [modelForm, setModelForm] = useState({ brand_id: '', category: 'pos', model_name: '', manufacturer_model_code: '' })

  useEffect(() => { load() }, [])

  async function load() {
    try {
      setLoading(true)
      setError('')
      const [articlesRes, brandsRes, modelsRes, servicesRes] = await Promise.all([
        supabase.rpc('codevaro_knowledge_admin_list'),
        supabase.from('codevaro_asset_brands').select('*').order('name'),
        supabase.from('codevaro_asset_models').select('*').eq('is_active', true).order('model_name'),
        supabase.from('codevaro_services').select('id,title,slug').order('title')
      ])
      for (const result of [articlesRes, brandsRes, modelsRes, servicesRes]) if (result.error) throw result.error
      setRows(articlesRes.data || [])
      setBrands(brandsRes.data || [])
      setModels(modelsRes.data || [])
      setServices(servicesRes.data || [])
    } catch (err) {
      console.error(err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const brandById = useMemo(() => Object.fromEntries(brands.map((x) => [x.id, x])), [brands])
  const types = useMemo(() => Array.from(new Set(rows.map((x) => x.article_type).filter(Boolean))).sort(), [rows])
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((row) => {
      const matchesType = type === 'all' || row.article_type === type
      const haystack = [row.title, row.content, row.service_title, row.brand_name, row.model_name, ...(row.tags || [])].filter(Boolean).join(' ').toLowerCase()
      return matchesType && (!q || haystack.includes(q))
    })
  }, [rows, search, type])

  function newArticle() {
    setForm(blankArticle())
    setEditorOpen(true)
  }

  async function editArticle(row) {
    try {
      setError('')
      const { data, error: readError } = await supabase.from('codevaro_knowledge_articles').select('*').eq('id', row.id).single()
      if (readError) throw readError
      setForm({
        ...blankArticle(),
        ...data,
        service_id: data.service_id || '',
        asset_model_id: data.asset_model_id || '',
        document_url: data.document_url || '',
        source_url: data.source_url || '',
        version: data.version || '',
        tags: (data.tags || []).join(', ')
      })
      setEditorOpen(true)
    } catch (err) {
      console.error(err)
      setError(`Αποτυχία φόρτωσης άρθρου: ${err.message}`)
    }
  }

  async function saveArticle(e) {
    e.preventDefault()
    if (!form.title.trim()) {
      setError('Ο τίτλος είναι υποχρεωτικός.')
      return
    }
    try {
      setSaving(true)
      setError('')
      const payload = {
        service_id: form.service_id || null,
        asset_model_id: form.asset_model_id || null,
        article_type: form.article_type || 'howto',
        title: form.title.trim(),
        content: form.content || null,
        document_url: form.document_url || null,
        source_url: form.source_url || null,
        version: form.version || null,
        tags: form.tags.split(',').map((x) => x.trim()).filter(Boolean),
        is_active: true
      }
      const result = form.id
        ? await supabase.from('codevaro_knowledge_articles').update(payload).eq('id', form.id)
        : await supabase.from('codevaro_knowledge_articles').insert(payload)
      if (result.error) throw result.error
      setEditorOpen(false)
      setForm(blankArticle())
      await load()
    } catch (err) {
      console.error(err)
      setError(`Αποτυχία αποθήκευσης: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  async function addBrand() {
    const name = brandName.trim()
    if (!name) return
    try {
      setError('')
      const { error: insertError } = await supabase.from('codevaro_asset_brands').insert({ name })
      if (insertError) throw insertError
      setBrandName('')
      await load()
    } catch (err) {
      console.error(err)
      setError(`Αποτυχία brand: ${err.message}`)
    }
  }

  async function addModel() {
    if (!modelForm.model_name.trim() || !modelForm.category.trim()) {
      setError('Model name και category είναι υποχρεωτικά.')
      return
    }
    try {
      setError('')
      const { error: insertError } = await supabase.from('codevaro_asset_models').insert({
        brand_id: modelForm.brand_id || null,
        category: modelForm.category.trim(),
        model_name: modelForm.model_name.trim(),
        manufacturer_model_code: modelForm.manufacturer_model_code.trim() || null
      })
      if (insertError) throw insertError
      setModelForm({ brand_id: '', category: 'pos', model_name: '', manufacturer_model_code: '' })
      await load()
    } catch (err) {
      console.error(err)
      setError(`Αποτυχία model: ${err.message}`)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Knowledge / Manuals</h1>
          <p className="mt-1 text-sm text-slate-600">Wiki ανά service, brand και model: manuals, setup, troubleshooting, firmware notes και τεχνικές διαδικασίες.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Model, manual, procedure..." className="min-w-[280px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm"><option value="all">All types</option>{types.map((x) => <option key={x} value={x}>{x}</option>)}</select>
          <button onClick={() => setCatalogOpen((v) => !v)} className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium hover:bg-slate-50"><SafeIcon icon={FiIcons.FiCpu} className="h-4 w-4" />Models</button>
          <button onClick={newArticle} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"><SafeIcon icon={FiIcons.FiPlus} className="h-4 w-4" />New Article</button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600"><strong>Structure:</strong> one model can have many documents and articles. Manufacturer PDFs belong as <code>manual</code>, field procedures as <code>howto</code>, known faults as <code>troubleshooting</code>, and bank/provider/network constants as <code>configuration</code>.</div>

      {catalogOpen ? (
        <section className="grid grid-cols-1 gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm xl:grid-cols-2">
          <div>
            <h2 className="font-semibold text-slate-900">Brands</h2>
            <div className="mt-3 flex gap-2"><input value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="Ingenico, RBS, PAX..." className={inputClass} /><button onClick={addBrand} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white">Add</button></div>
            <div className="mt-3 flex flex-wrap gap-2">{brands.map((brand) => <span key={brand.id} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">{brand.name}</span>)}</div>
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Models</h2>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <select value={modelForm.brand_id} onChange={(e) => setModelForm((f) => ({ ...f, brand_id: e.target.value }))} className={inputClass}><option value="">No brand</option>{brands.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
              <input value={modelForm.category} onChange={(e) => setModelForm((f) => ({ ...f, category: e.target.value }))} placeholder="pos / cash_register / router" className={inputClass} />
              <input value={modelForm.model_name} onChange={(e) => setModelForm((f) => ({ ...f, model_name: e.target.value }))} placeholder="Model name" className={inputClass} />
              <input value={modelForm.manufacturer_model_code} onChange={(e) => setModelForm((f) => ({ ...f, manufacturer_model_code: e.target.value }))} placeholder="Manufacturer code" className={inputClass} />
            </div>
            <button onClick={addModel} className="mt-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white">Add model</button>
            <div className="mt-3 max-h-44 space-y-1 overflow-y-auto">{models.map((model) => <div key={model.id} className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700"><strong>{brandById[model.brand_id]?.name || '—'} {model.model_name}</strong> · {model.category}{model.manufacturer_model_code ? ` · ${model.manufacturer_model_code}` : ''}</div>)}</div>
          </div>
        </section>
      ) : null}

      {error ? <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
      {loading ? <div className="flex h-48 items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" /></div> : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center"><SafeIcon icon={FiIcons.FiBookOpen} className="mx-auto h-12 w-12 text-slate-300" /><h2 className="mt-3 font-semibold text-slate-700">Knowledge base ready</h2><p className="mt-1 text-sm text-slate-500">Add the first normalized manual, setup guide or troubleshooting article.</p></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">{filtered.map((row) => <button key={row.id} onClick={() => editArticle(row)} className="rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md"><div className="flex items-start justify-between gap-4"><div><div className="mb-2 flex flex-wrap gap-2"><span className="rounded-full bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-700">{row.article_type || 'article'}</span>{row.service_title ? <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs text-blue-700">{row.service_title}</span> : null}</div><h2 className="text-lg font-semibold text-slate-900">{row.title}</h2><p className="mt-1 text-sm text-slate-500">{[row.brand_name, row.model_name].filter(Boolean).join(' ') || 'General knowledge'}</p></div><SafeIcon icon={row.article_type === 'manual' ? FiIcons.FiFileText : FiIcons.FiBookOpen} className="h-6 w-6 text-slate-400" /></div>{row.content ? <p className="mt-4 line-clamp-6 whitespace-pre-wrap text-sm text-slate-700">{row.content}</p> : null}<div className="mt-4 flex flex-wrap gap-2">{(row.tags || []).map((tag) => <span key={tag} className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-600">{tag}</span>)}</div>{row.document_url ? <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-blue-600"><SafeIcon icon={FiIcons.FiExternalLink} className="h-4 w-4" />Manual/document linked</span> : null}</button>)}</div>
      )}

      {editorOpen ? (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/50 p-3 backdrop-blur-sm sm:p-6">
          <form onSubmit={saveArticle} className="mx-auto max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4"><div><h2 className="text-xl font-semibold text-slate-900">{form.id ? 'Edit Knowledge Article' : 'New Knowledge Article'}</h2><p className="text-sm text-slate-500">Attach it to a service, model, or keep it general.</p></div><button type="button" onClick={() => setEditorOpen(false)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><SafeIcon icon={FiIcons.FiX} className="h-5 w-5" /></button></div>
            <div className="space-y-4 p-5 sm:p-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Title *"><input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={inputClass} /></Field>
                <Field label="Type"><select value={form.article_type} onChange={(e) => setForm((f) => ({ ...f, article_type: e.target.value }))} className={inputClass}><option value="manual">Manual</option><option value="howto">How-to</option><option value="troubleshooting">Troubleshooting</option><option value="configuration">Configuration</option><option value="firmware">Firmware</option><option value="known_issue">Known issue</option><option value="reference">Reference</option></select></Field>
                <Field label="Service"><select value={form.service_id} onChange={(e) => setForm((f) => ({ ...f, service_id: e.target.value }))} className={inputClass}><option value="">General</option>{services.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}</select></Field>
                <Field label="Equipment model"><select value={form.asset_model_id} onChange={(e) => setForm((f) => ({ ...f, asset_model_id: e.target.value }))} className={inputClass}><option value="">Any model</option>{models.map((x) => <option key={x.id} value={x.id}>{[brandById[x.brand_id]?.name, x.model_name].filter(Boolean).join(' ')}</option>)}</select></Field>
                <Field label="Version"><input value={form.version} onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))} className={inputClass} /></Field>
                <Field label="Tags (comma separated)"><input value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} className={inputClass} placeholder="ict220, epay, ethernet" /></Field>
                <Field label="Document URL"><input value={form.document_url} onChange={(e) => setForm((f) => ({ ...f, document_url: e.target.value }))} className={inputClass} placeholder="https://...pdf" /></Field>
                <Field label="Source URL"><input value={form.source_url} onChange={(e) => setForm((f) => ({ ...f, source_url: e.target.value }))} className={inputClass} placeholder="Manufacturer/provider source" /></Field>
              </div>
              <Field label="Article / procedure"><textarea value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} rows={14} className={inputClass} placeholder="Steps, menu path, IP/port values, symptoms, solution, cautions..." /></Field>
            </div>
            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 px-5 py-4 sm:flex-row sm:justify-end"><button type="button" onClick={() => setEditorOpen(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">Cancel</button><button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white disabled:opacity-50">{saving ? 'Saving…' : 'Save Article'}</button></div>
          </form>
        </div>
      ) : null}
    </div>
  )
}

function Field({ label, children }) { return <label><div className="mb-1 text-xs font-medium text-slate-500">{label}</div>{children}</label> }